import { IStyle } from 'Styles';
import { LabelRadioBox } from 'components/customComponents/Layers/InputGroups';
import { CustomCheckBox } from 'components/input';
import React, { useEffect, useRef } from 'react';
import {
  ActionOperationType,
  ActionOperationValue,
  ExcludeInspections,
  JobTypeDTO,
} from './constants';
import { ImageManagementActionDTO } from 'business/models/Models';
import { getCurrentOperationType } from './utils';
import { useRMDispatch } from 'redux/hooks';
import {
  deleteConstraintRuleByFieldId,
  onAddNewImageTask,
  onAddNewTaskAction,
  onDeleteImageTask,
  onDeleteTaskAction,
  setCheckedExplorerNodes,
  setDeletedActionIds,
  setImageManagementTaskList,
  setImageManagementTaskOriginalList,
  setImageManagementTaskResultList,
  setIsTaskRunning,
  setIsPreview,
  setSelectedImageActionId,
  setSelectedImageTaskId,
  updateConstraintRules,
  updateImageActionConstraints,
  updateSelectedImageTask,
  updateSelectedImageTaskActions,
  setIsError,
  updateIsImageManagementTaskResultEmpty,
  updateSelectedImageTaskResultsBatch,
  setClearResults,
} from 'redux/reducers/imageManagementTaskReducer';
import {
  saveImageManagementTasks,
  startImageManagementTasks,
} from 'redux/thunks/imageManagementTask';
import { cloneDeep } from 'lodash';
import { getImageStoreInfo } from 'redux/thunks/GeneralSetup';
import { removeBackGroundJob } from 'redux/reducers/backgroundJobsReducer';

const actionsList = [
  ActionOperationType[ActionOperationValue.Move],
  ActionOperationType[ActionOperationValue.Copy],
  ActionOperationType[ActionOperationValue.Delete],
];

const inpectionsList = [
  ExcludeInspections.FIRSTINSPECTION,
  ExcludeInspections.LASTINSPECTION,
];

export const renderRadioButtons = (selectedAction, onChangeRadioActions) => {
  const selectedRadioActionType = getCurrentOperationType(selectedAction);
  return (
    <>
      {actionsList.map((value: string, i) => (
        <LabelRadioBox
          key={`imlrb${i}`}
          labelText={value}
          radioValue={value === selectedRadioActionType ? true : false}
          style={styles.radioButton}
          onChange={() => onChangeRadioActions(value)}
          name={value}
          disabled={!selectedAction}
        />
      ))}
    </>
  );
};

export const renderInspectionCheckBoxes = (
  selectedAction: ImageManagementActionDTO,
  onChangeInspectionCheckBoxValue,
) => {
  return (
    <>
      {inpectionsList.map((value: string, i) => (
        <CustomCheckBox
          key={`imtcb${i}`}
          onChange={() => onChangeInspectionCheckBoxValue(value, selectedAction)}
          label={value}
          checked={
            value === ExcludeInspections.FIRSTINSPECTION
              ? selectedAction?.excludeFirstInspection
              : selectedAction?.excludeLastInspection
          }
          mainContainerStyle={styles.checkBox}
          labelStyle={{
            fontFamily: 'Roboto',
            fontWeight: 400,
            marginLeft: '0.75rem',
            width: '100%',
          }}
          disabled={!selectedAction}
        />
      ))}
    </>
  );
};

export const useOnNodeSelectionChange = () => {
  const dispatch = useRMDispatch();
  return (selectedNodesIds) => {
    dispatch(setCheckedExplorerNodes(selectedNodesIds));
  };
};

export const updateImageTasksList = () => {
  const dispatch = useRMDispatch();
  return (tasksList) => {
    dispatch(setImageManagementTaskList(tasksList));
  };
};

export const updateImageTaskResultsList = () => {
  const dispatch = useRMDispatch();
  return (tasksList) => {
    dispatch(setImageManagementTaskResultList(tasksList));
  };
};

export const useImageManagementClearResults = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(setClearResults());
  };
};

export const updateIsImageTaskResultEmpty = () => {
  const dispatch = useRMDispatch();
  return (value) => {
    dispatch(updateIsImageManagementTaskResultEmpty(value));
  };
};

export const updateOriginalImageTasksList = () => {
  const dispatch = useRMDispatch();
  return (tasksList) => {
    dispatch(setImageManagementTaskOriginalList(cloneDeep([...tasksList])));
  };
};

export const updateDeletedActionIdsList = () => {
  const dispatch = useRMDispatch();
  return (idsList) => {
    dispatch(setDeletedActionIds(idsList));
  };
};

export const useOnChangeAction = () => {
  const dispatch = useRMDispatch();
  return (value) => {
    dispatch(setSelectedImageActionId(value?.id));
  };
};

export const useOnChangeRadioActions = () => {
  const dispatch = useRMDispatch();
  return (value) => {
    dispatch(
      updateSelectedImageTaskActions({
        key: 'actionType',
        value: ActionOperationValue[value],
      }),
    );
  };
};

export const useOnChangeVerifyValue = () => {
  const dispatch = useRMDispatch();
  return (e) => {
    dispatch(updateSelectedImageTaskActions({ key: 'verify', value: !e.target.checked }));
  };
};

export const useOnChangeImageMinAgeValue = () => {
  const dispatch = useRMDispatch();
  return (value: number) => {
    dispatch(
      updateSelectedImageTaskActions({
        key: 'minImageAge',
        value,
      }),
    );
  };
};

export const useOnChangeSourceImageStoreValue = () => {
  const dispatch = useRMDispatch();
  return (value) => {
    dispatch(updateSelectedImageTaskActions({ key: 'sourceImageStoreId', value: value }));
  };
};

export const useOnChangeTargetImageStoreValue = () => {
  const dispatch = useRMDispatch();
  return (value) => {
    dispatch(updateSelectedImageTaskActions({ key: 'targetImageStoreId', value: value }));
  };
};

export const useOnChangeInspectionCheckBoxValue = () => {
  const dispatch = useRMDispatch();
  return (value, selectedAction) => {
    if (value === ExcludeInspections.FIRSTINSPECTION) {
      dispatch(
        updateSelectedImageTaskActions({
          key: 'excludeFirstInspection',
          value: !selectedAction.excludeFirstInspection,
        }),
      );
    } else if (value === ExcludeInspections.LASTINSPECTION) {
      dispatch(
        updateSelectedImageTaskActions({
          key: 'excludeLastInspection',
          value: !selectedAction.excludeLastInspection,
        }),
      );
    }
  };
};

export const useOnUpdateSelectedImageActionId = () => {
  const dispatch = useRMDispatch();
  return (id) => {
    dispatch(setSelectedImageActionId(id));
  };
};

export const useOnAddNewAction = () => {
  const dispatch = useRMDispatch();
  return (newAction) => {
    dispatch(onAddNewTaskAction(newAction));
    dispatch(setSelectedImageActionId(newAction?.id));
  };
};

export const useOnSaveImageTasks = () => {
  const dispatch = useRMDispatch();
  return (saveImageTaskDTO) => {
    dispatch(saveImageManagementTasks(saveImageTaskDTO));
  };
};

export const useOnDeleteAction = () => {
  const dispatch = useRMDispatch();
  return (id) => {
    dispatch(onDeleteTaskAction(id));
  };
};

export const useOnAddNewImageTask = () => {
  const dispatch = useRMDispatch();
  return (newTask) => {
    dispatch(onAddNewImageTask(newTask));
  };
};

export const useOnDeleteImageTask = () => {
  const dispatch = useRMDispatch();
  return (id) => {
    dispatch(onDeleteImageTask(id));
    dispatch(removeBackGroundJob({ id: id, type: JobTypeDTO.ImageManangement }));
  };
};

export const useOnSetSelectedImageManagementTaskId = () => {
  const dispatch = useRMDispatch();
  return (id) => {
    dispatch(setSelectedImageTaskId(id));
  };
};

export const useOnChangeImageTaskValue = () => {
  const dispatch = useRMDispatch();
  return (key, value) => {
    dispatch(updateSelectedImageTask({ key: key, value: value }));
  };
};

export const useOnChangeImageActions = () => {
  const dispatch = useRMDispatch();
  return (key, value) => {
    dispatch(updateSelectedImageTaskActions({ key: key, value: value }));
  };
};

export const useOnChangeActionConstraints = () => {
  const dispatch = useRMDispatch();
  return (constraints) => {
    dispatch(updateImageActionConstraints(constraints));
  };
};

export const useOnChangeConstraintRules = () => {
  const dispatch = useRMDispatch();
  return (field, value) => {
    dispatch(updateConstraintRules({ field: field, value: value }));
  };
};

export const useOnDeleteConstraintRule = () => {
  const dispatch = useRMDispatch();
  return (field) => {
    dispatch(deleteConstraintRuleByFieldId(field));
  };
};

export const useOnStartImageTask = () => {
  const dispatch = useRMDispatch();
  return (startImageTaskDTO, disableProgress = false) => {
    return dispatch(startImageManagementTasks({ startImageTaskDTO, disableProgress }));
  };
};

export const useOnConnectionStart = () => {
  return (connection, jobId, setCurrentProgress) => {
    connection.on('UpdateJobProgress', (data) => {
      if (data.jobId == jobId) {
        setTimeout(() => setCurrentProgress(data), 1);
      }
    });
  };
};

export const useUpdateSelectedImageTaskResults = () => {
  const dispatch = useRMDispatch();
  const bufferRef = useRef<any[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (field: { key: string; value: any; selectedTaskId: number }) => {
    bufferRef.current.push(field);

    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        if (bufferRef.current.length > 0) {
          dispatch(updateSelectedImageTaskResultsBatch(bufferRef.current));
          bufferRef.current = [];
        }
      }, 100); // flush every 100ms (tweak as needed)
    }
  };
};

export const useUpdateIsSelectedTaskRunning = () => {
  const dispatch = useRMDispatch();
  return (field) => {
    dispatch(setIsTaskRunning(field));
  };
};

export const useUpdateIsPreview = () => {
  const dispatch = useRMDispatch();
  return (field) => {
    dispatch(setIsPreview(field));
  };
};

export const useUpdateIsError = () => {
  const dispatch = useRMDispatch();
  return (field) => {
    dispatch(setIsError(field));
  };
};

export const useFetchImageStorage = () => {
  const dispatch = useRMDispatch();
  return () => {
    dispatch(getImageStoreInfo());
  };
};

const styles: IStyle = {
  checkBox: {
    marginTop: '2%',
    marginBottom: '2%',
    width: '50%',
    justifyContent: 'flex-start',
  },
  radioButton: {
    width: '50%',
    justifyContent: 'flex-start',
    fontFamily: 'Roboto',
    fontWeight: 400,
  },
};
